import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AddUpdateTemplateViewModel } from "../../types/viewModels/AddUpdateTemplateViewModel";
import type { TemplateDTO } from "../../types/dtos/TemplateDTO";
import { useApiAccess } from "@repo/hooks/contexts";
import type { ExecutionResponse } from "@repo/types/responses";
import { templateKeys } from "@repo/types/queries";

export default function useAddUpdateTemplate() {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      model: AddUpdateTemplateViewModel,
    ): Promise<ExecutionResponse> => {
      const res = await apiClient.post<ExecutionResponse>(
        "/Template/addUpdate",
        model,
      );

      return res.data;
    },
    onSuccess: () => {
      // Invalidate templates list cache on successful add/update
      templateKeys.invalidateList(queryClient);
    },
  });
}

export function useGetTemplates<T = TemplateDTO>(
  select?: (data: TemplateDTO[]) => T[],
) {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: templateKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<TemplateDTO[]>(`/Template/get`);
      return response.data;
    },
    select,
  });
}

export function useGetTemplateById<T = TemplateDTO>(
  id: number,
  select?: (data: TemplateDTO) => T,
) {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: templateKeys.details(id),
    queryFn: async () => {
      const response = await apiClient.get<TemplateDTO>(`/Template/get/${id}`);
      return response.data;
    },
    select,
  });
}
